import type React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { CostpointReport } from '../../types/reporting.types';
import { formatEuro } from '../../utils/currency';

interface BudgetVsActualChartProps {
  data: CostpointReport[];
  height?: number;
}

const BudgetVsActualChart: React.FC<BudgetVsActualChartProps> = ({ data, height = 400 }) => {
  // Transform data for income and expense charts
  const incomeChartData = data.map((report) => ({
    costpoint: report.costpoint.toUpperCase(),
    budgeted: report.budget_summary.total_income_budgeted,
    actual: report.actual_summary.total_income_actual,
    variance:
      report.actual_summary.total_income_actual - report.budget_summary.total_income_budgeted,
    performance: report.variance_analysis.overall_performance,
  }));

  const expenseChartData = data.map((report) => ({
    costpoint: report.costpoint.toUpperCase(),
    budgeted: report.budget_summary.total_expenses_budgeted,
    actual: report.actual_summary.total_expenses_actual,
    variance:
      report.budget_summary.total_expenses_budgeted - report.actual_summary.total_expenses_actual,
    performance: report.variance_analysis.overall_performance,
  }));

  // Custom tooltip
  type ChartEntry = {
    costpoint: string;
    budgeted: number;
    actual: number;
    variance: number;
    performance: string;
  };
  const CustomTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: { payload: ChartEntry }[];
    label?: string;
  }) => {
    if (active && payload?.length && payload[0]) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-900">{`${label} Costpoint`}</p>
          <p className="text-blue-600">
            <span className="font-medium">Budgeted:</span> {formatEuro(data.budgeted)}
          </p>
          <p className="text-green-600">
            <span className="font-medium">Actual:</span> {formatEuro(data.actual)}
          </p>
          <p className={`font-medium ${data.variance >= 0 ? 'text-red-600' : 'text-green-600'}`}>
            <span>Variance:</span> {formatEuro(data.variance)}
          </p>
          <p className="text-sm text-gray-600 capitalize">
            Status: {data.performance ? data.performance.replace('_', ' ') : 'Unknown'}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Budget vs Actual by Costpoint</h3>
        <p className="text-sm text-gray-600">Income and Expenses breakdown</p>
      </div>

      {/* Income Chart */}
      <div className="mb-6">
        <h4 className="text-md font-medium text-gray-900 mb-2">Income</h4>
        <ResponsiveContainer width="100%" height={height / 2}>
          <BarChart
            data={incomeChartData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis dataKey="costpoint" tick={{ fontSize: 12 }} tickLine={{ stroke: '#6B7280' }} />
            <YAxis
              tick={{ fontSize: 12 }}
              tickLine={{ stroke: '#6B7280' }}
              tickFormatter={formatEuro}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="budgeted" fill="#3B82F6" name="Budgeted" radius={[2, 2, 0, 0]} />
            <Bar dataKey="actual" fill="#10B981" name="Actual" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Expenses Chart */}
      <div className="mb-6">
        <h4 className="text-md font-medium text-gray-900 mb-2">Expenses</h4>
        <ResponsiveContainer width="100%" height={height / 2}>
          <BarChart
            data={expenseChartData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis dataKey="costpoint" tick={{ fontSize: 12 }} tickLine={{ stroke: '#6B7280' }} />
            <YAxis
              tick={{ fontSize: 12 }}
              tickLine={{ stroke: '#6B7280' }}
              tickFormatter={formatEuro}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="budgeted" fill="#3B82F6" name="Budgeted" radius={[2, 2, 0, 0]} />
            <Bar dataKey="actual" fill="#F97316" name="Actual" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Readable Numbers Summary */}
      <div className="mt-4 space-y-4">
        {data.map((report) => (
          <div key={report.costpoint} className="bg-gray-50 rounded-lg p-4">
            <h5 className="font-medium text-gray-900 mb-3">{report.costpoint.toUpperCase()}</h5>

            {/* Income Row */}
            <div className="grid grid-cols-5 gap-4 text-sm mb-2">
              <div className="text-gray-600">Income:</div>
              <div className="text-blue-600 font-medium">
                {formatEuro(report.budget_summary.total_income_budgeted)}
              </div>
              <div className="text-green-600 font-medium">
                {formatEuro(report.actual_summary.total_income_actual)}
              </div>
              <div
                className={`font-medium ${
                  report.actual_summary.total_income_actual -
                    report.budget_summary.total_income_budgeted >=
                  0
                    ? 'text-green-600'
                    : 'text-red-600'
                }`}
              >
                {report.actual_summary.total_income_actual -
                  report.budget_summary.total_income_budgeted >=
                0
                  ? '+'
                  : ''}
                {formatEuro(
                  Math.abs(
                    report.actual_summary.total_income_actual -
                      report.budget_summary.total_income_budgeted
                  )
                )}
              </div>
              <div></div> {/* Empty cell for Net column in Income row */}
            </div>

            {/* Expenses Row */}
            <div className="grid grid-cols-5 gap-4 text-sm mb-2">
              <div className="text-gray-600">Expenses:</div>
              <div className="text-blue-600 font-medium">
                {formatEuro(report.budget_summary.total_expenses_budgeted)}
              </div>
              <div className="text-orange-600 font-medium">
                {formatEuro(report.actual_summary.total_expenses_actual)}
              </div>
              <div
                className={`font-medium ${
                  report.budget_summary.total_expenses_budgeted -
                    report.actual_summary.total_expenses_actual >=
                  0
                    ? 'text-green-600'
                    : 'text-red-600'
                }`}
              >
                {report.budget_summary.total_expenses_budgeted -
                  report.actual_summary.total_expenses_actual >=
                0
                  ? '+'
                  : ''}
                {formatEuro(
                  Math.abs(
                    report.budget_summary.total_expenses_budgeted -
                      report.actual_summary.total_expenses_actual
                  )
                )}
              </div>
              {/* Net Column - Actual Income minus Actual Expenses */}
              <div
                className={`font-medium ${
                  report.actual_summary.total_income_actual -
                    report.actual_summary.total_expenses_actual >=
                  0
                    ? 'text-green-600'
                    : 'text-red-600'
                }`}
              >
                {report.actual_summary.total_income_actual -
                  report.actual_summary.total_expenses_actual >=
                0
                  ? '+'
                  : ''}
                {formatEuro(
                  Math.abs(
                    report.actual_summary.total_income_actual -
                      report.actual_summary.total_expenses_actual
                  )
                )}
              </div>
            </div>

            {/* Column Headers */}
            <div className="grid grid-cols-5 gap-4 text-xs text-gray-400 mt-3 pt-2 border-t">
              <div></div>
              <div>Budgeted</div>
              <div>Actual</div>
              <div>Variance</div>
              <div>Net</div>
            </div>
          </div>
        ))}
      </div>

      {/* Performance Summary */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
          <span>
            On Target:{' '}
            {data.filter((d) => d.variance_analysis.overall_performance === 'on_target').length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
          <span>
            Over Budget:{' '}
            {data.filter((d) => d.variance_analysis.overall_performance === 'over_budget').length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          <span>
            Under Budget:{' '}
            {data.filter((d) => d.variance_analysis.overall_performance === 'under_budget').length}
          </span>
        </div>
      </div>
    </div>
  );
};

export default BudgetVsActualChart;
